/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { DefaultPalette, IColumn, ICommandBarItemProps, Icon, TooltipHost } from '@fluentui/react';
import { t } from 'i18next';
import { DetailsListView } from '../Common/DetailsList/DetailListView';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { defaultCommandBarButtonStyles } from '../../styles/button-styles';
import TimeTaxSupportSystemClient from '../../system';
import { User } from '../../system/User';
import { GRID_MODE, OPERATORS, TYPES } from '../../common/list-utils';
import MessageCourier from '../../lib/MessageCourier';
import Loading from '../Spinners/Loading';
import { normalizeEmails } from '../../common/normalizeEmails';

const itemsMenu: ICommandBarItemProps[] = [
  {
    key: 'title',
    text: t('Notificaciónes por área'),
    iconProps: {
      iconName: 'MailOptions',
      color: DefaultPalette.themeLighterAlt,
      className: 'text-title-btn',
    },
    className: 'text-title-btn',
  },
];

export interface INotificationByHierarchies {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (change: boolean) => void;
  user: User;
  path: string;
}

export default function NotificationByHierarchies(props: INotificationByHierarchies) {
  const [listLevelsColumns, setListLevesColumns] = useState<Partial<IColumn>[]>([]);
  const [listLevelsFilters, setListLevelsFilters] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    const getLevelsNodes = async () => {
      setLoading(true);
      const respuesta = await props.system?.getOrganizationChartSystem().getLevels();
      const formattedLevels = await Promise.all(
        respuesta.map(async (item) => {
          const organigramaData = await props.system?.getOrganizationChartSystem().getLastLevelNodesss(item.getLevel().toString());
          return {
            quick: true,
            name: item.getNameTec(),
            type: TYPES.COMBOBOXTEXT,
            placeholder: t(item.getName()),
            operator: OPERATORS.CONTAINS,
            contextForCombobox: organigramaData.levels,
          };
        }),
      );

      setListLevelsFilters(formattedLevels);
      setListLevesColumns([
        ...respuesta.map((item) => ({
          fieldName: item.getNameTec(),
          name: t(item.getName()),
        })),
        {
          minWidth: 200,
          onRenderHeader: () => {
            return (
              <div className="notification-email-header">
                <TooltipHost content={t("Si se ingresa más de 1 correo separar por una coma (,)")}>
                  <Icon iconName="Info" />
                </TooltipHost>
                <span>{t('Notificación directa')}</span>
              </div>
            );
          },
          onColumnClick: () => {},
          onRenderField: (item) => {
            if (!item) return null;
            return (
              <form
                className="notification-email-container"
                noValidate
                key={item.item.nor_id + item.item.nor_nivel}
                onSubmit={async (e) => {
                  e.preventDefault();
                  props.courier.messageError('');

                  const values = Object.fromEntries(new FormData(e.currentTarget).entries());

                  const nors_mails = values.nors_mails.toString().trim();

                  if ((!nors_mails && !item.item.nors_id) || (!item.item.nors_mails && !nors_mails && item.item.nors_id)) {
                    props.courier.messageError(t('Debes ingresar al menos un correo electrónico'));
                    return;
                  }

                  // Regex que valida los correos electrónicos
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  const emails = nors_mails.split(',');
                  const invalidEmails = emails.filter((email) => !emailRegex.test(email.trim()));

                  if (invalidEmails.length > 0 && nors_mails !== '') {
                    props.courier.messageError(t(invalidEmails.length > 1 ? 'Algunos de los correos que has ingresado son inválidos' : 'Un correo que has ingresado es inválido'));
                    return;
                  }

                  if (nors_mails === item.item.nors_mails) {
                    props.courier.messageSuccess(t('No se han realizado cambios en los correos electrónicos'));
                    return;
                  }

                  props.changeBlockUI(true);

                  const cleanEmails = normalizeEmails(nors_mails);

                  const ctrl = props.system.getNodoOrganigramaSeguimientoSystem();
                  setRefresh(false);
                  const result = await ctrl.upsert({
                    nors_id: item.item.nors_id,
                    nors_norid: item.item.nor_id,
                    nors_mails: cleanEmails,
                  });
                  setRefresh(true);

                  if (!result.data.nors_mails) {
                    props.courier.messageSuccess(t('Se actualizo el registro sin correos electrónicos'));
                  } else {
                    props.courier.messageSuccess(t(emails.length > 1 ? 'Correos electrónicos guardados correctamente' : 'Correo electrónico guardado correctamente'));
                  }

                  // Actualizar item con los nuevos valores
                  item.item.nors_mails = result.data.nors_mails;
                  item.item.nors_id = result.data.nors_id;

                  props.changeBlockUI(false);
                }}
              >
                <Icon iconName="Mail" className="notification-email-icon" />
                <input className="notification-email-input" type="text" defaultValue={item.item.nors_mails} placeholder={t('Ingrese el o los correos a notificar')} name="nors_mails" id={`nors_mails_${item.item.nors_id}`} />
                <button
                  className="notification-email-button"
                  type="submit"
                  style={{
                    background: item.item.nors_id && item.item.nors_mails ? '#0078d4' : '#2d97ea',
                  }}
                >
                  {t(item.item.nors_id && item.item.nors_mails ? 'Actualizar' : 'Guardar')}
                </button>
              </form>
            );
          },
        },
      ]);
      setLoading(false);
    };
    getLevelsNodes();
  }, []);

  if (loading) {
    return <Loading title="Cargando niveles..." description="Por favor, espere un momento, estamos obteniendo los niveles de cada area de organigrama." />;
  }

  return (
    <ViewWrapperWithCommandBar withBorderBottom title={''} items={itemsMenu} overflowButtonStyles={defaultCommandBarButtonStyles} variant="standard">
      <DetailsListView
        refresh={refresh}
        keyList={props.path}
        onClickCardMode={() => {}}
        onClickGridMode={() => {}}
        mode={GRID_MODE}
        title={t('Áreas')}
        cols={listLevelsColumns}
        isGridList={true}
        listFilters={[]}
        quickFilters={listLevelsFilters}
        baseUrl={props.system.getOrganizationChartSystem().getBaseUrlForDatagrid()}
        changeBlockUI={props.changeBlockUI}
        courier={props.courier}
      ></DetailsListView>
    </ViewWrapperWithCommandBar>
  );
}
